import { Controller } from "stimulus"

import { initCandidateTags } from "../candidate_tags"
import { readNoteInput } from "../candidate_notes_log"
import { rowsFor, syncRowNotes } from "../candidate_row_sync"
import { CallBriefSession } from "../phone_dashboard/call_brief"
import { Dialer } from "../phone_dashboard/dialer"
import { showToast } from "../utils/toast"
import type { InviteAnswer } from "./interview_invite_controller"
import { mountLogos } from "../utils/bots_prospects/mount_logo"

type Outcome = "no_answer" | "maybe_later" | "callback" | "not_interested" | "interview_booked"
type StatKey = "interview_booked" | "callback" | "no_answer" | "not_interested" | "tasks" | "invites"

interface FollowUpTask { title: string; date: string; time: string; who: string; note: string }

// The task dialog's follow-up mode takes the stored status_calling value.
const FOLLOW_UP: Partial<Record<Outcome, string>> = {
  no_answer: "no_response",
  maybe_later: "maybe_later",
  callback: "follow_up_later"
}

const STAT_FOR: Record<Outcome, StatKey> = {
  interview_booked: "interview_booked",
  callback: "callback",
  no_answer: "no_answer",
  maybe_later: "no_answer",
  not_interested: "not_interested"
}

const SPINNER = '<div class="call-loading"><div class="spinner-border text-primary" role="status"></div></div>'

export default class extends Controller {
  static targets = [
    "backdrop", "panel", "index", "total", "fill", "body", "actions",
    "done", "doneHeading", "donePaused", "doneFinished", "doneProgress", "resume"
  ]
  static values = { cardUrl: String, outcomeUrl: String, briefUrl: String }

  declare readonly backdropTarget: HTMLElement
  declare readonly panelTarget: HTMLElement
  declare readonly indexTarget: HTMLElement
  declare readonly totalTarget: HTMLElement
  declare readonly fillTarget: HTMLElement
  declare readonly bodyTarget: HTMLElement
  declare readonly actionsTarget: HTMLElement
  declare readonly doneTarget: HTMLElement
  declare readonly doneHeadingTarget: HTMLElement
  declare readonly donePausedTarget: HTMLElement
  declare readonly doneFinishedTarget: HTMLElement
  declare readonly doneProgressTarget: HTMLElement
  declare readonly resumeTarget: HTMLElement
  declare readonly cardUrlValue: string
  declare readonly outcomeUrlValue: string
  declare readonly briefUrlValue: string
  declare readonly hasBriefUrlValue: boolean

  private queue: string[] = []
  private botIds: string[] = []
  private briefs: CallBriefSession | null = null
  private pos = 0
  private logged = 0
  private stats: Record<StatKey, number> = this.emptyStats()
  private dialer: Dialer | null = null
  private busy = false
  private loadSeq = 0

  disconnect(): void {
    this.teardownCard()
  }

  open(event: CustomEvent<{ ids: string[]; botIds?: string[] }>): void {
    const ids = event.detail?.ids || []
    if (!ids.length) return

    // the drawer carries the same fixed-id tag and note blocks; empty it so none collide
    const drawer = document.getElementById("candidate-drawer")
    if (drawer) drawer.innerHTML = ""

    this.queue = ids.slice()
    // botIds[i] is queue[i]'s campaign; the brief URL is only rendered for admins
    this.botIds = event.detail?.botIds || []
    this.briefs = this.hasBriefUrlValue ? new CallBriefSession(this.briefUrlValue, this.botIds) : null
    this.pos = 0
    this.logged = 0
    this.stats = this.emptyStats()
    this.showSteps()
    this.element.classList.add("open")
    this.panelTarget.setAttribute("aria-hidden", "false")
    this.render()
  }

  // ✕ (§3.5): with an outcome logged, show "Session paused"; otherwise just close.
  close(): void {
    if (this.doneShowing() || this.logged === 0) {
      this.dismiss()
      return
    }
    this.stopCall().then(() => this.finish(true))
  }

  // Esc, the backdrop and "Back to dashboard" close at once (the mockup's behaviour, R06).
  dismiss(): void {
    this.stopCall()
    this.teardownCard()
    this.element.classList.remove("open")
    this.panelTarget.setAttribute("aria-hidden", "true")
  }

  keydown(event: KeyboardEvent): void {
    if (event.key !== "Escape" || !this.isOpen()) return
    // the dialogs and the AI-analysis modal above the panel close first
    if (document.querySelector(".task-bd.open, .modal.show")) return

    this.dismiss()
  }

  beforeUnload(event: BeforeUnloadEvent): void {
    if (!this.dialer?.isLive()) return

    event.preventDefault()
    event.returnValue = ""
  }

  resume(): void {
    this.showSteps()
    this.render()
  }

  skip(): void {
    if (this.busy) return
    this.next()
  }

  retry(): void {
    this.render()
  }

  async pick(event: Event): Promise<void> {
    const outcome = (event.currentTarget as HTMLElement).dataset.outcome as Outcome | undefined
    if (!outcome || this.busy) return

    const card = this.card()
    if (!card) return

    this.busy = true
    try {
      await this.stopCall()
      const note = this.noteHtml(card)

      if (FOLLOW_UP[outcome]) {
        const answer = await this.askForTask(card, outcome, note)
        if (answer.cancelled) return
        await this.log(card, outcome, note, answer.task || null)
      } else if (outcome === "interview_booked") {
        await this.interviewBooked(card, note)
      } else {
        await this.log(card, outcome, note, null)
      }
    } finally {
      this.busy = false
    }
  }

  // --- steps ---------------------------------------------------------------

  private render(): void {
    if (this.pos >= this.queue.length) {
      this.finish(false)
      return
    }

    this.teardownCard()
    this.indexTarget.textContent = String(this.pos + 1)
    this.totalTarget.textContent = String(this.queue.length)
    this.fillTarget.style.width = `${(this.pos / this.queue.length) * 100}%`
    this.setActionsLocked(true)
    this.bodyTarget.innerHTML = SPINNER
    this.bodyTarget.scrollTop = 0

    const seq = ++this.loadSeq
    const id = this.queue[this.pos]
    fetch(this.cardUrlValue.replace(":id", encodeURIComponent(id)), {
      headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
    })
      .then(response => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then(html => {
        if (seq !== this.loadSeq) return
        this.bodyTarget.innerHTML = html
        const card = this.card()
        if (!card) throw new Error("empty card")

        initCandidateTags(card)
        mountLogos()
        const dialer = card.querySelector<HTMLElement>("[data-dialer]")
        if (dialer && dialer.querySelector("[data-dialer-call]")) this.dialer = new Dialer(dialer, id)
        const brief = card.querySelector<HTMLElement>("[data-call-brief]")
        if (brief && this.briefs) this.briefs.mount(brief, this.pos > 0 ? this.botIds[this.pos - 1] || null : null)
        this.setActionsLocked(false)
      })
      .catch(() => {
        if (seq !== this.loadSeq) return
        this.bodyTarget.innerHTML =
          '<div class="call-unavailable"><p>This candidate is no longer available.</p>' +
          '<div class="call-done-actions"><button type="button" class="btn" data-action="call-session#retry">Retry</button>' +
          '<button type="button" class="btn btn-ghost" data-action="call-session#skip">Skip</button></div></div>'
      })
  }

  private next(): void {
    this.pos += 1
    this.render()
  }

  private finish(paused: boolean): void {
    this.teardownCard()
    if (!paused) this.fillTarget.style.width = "100%"

    this.doneHeadingTarget.textContent = paused ? "Session paused" : "Session done"
    this.donePausedTarget.hidden = !paused
    this.doneFinishedTarget.hidden = paused
    this.doneProgressTarget.textContent =
      `${this.logged} of ${this.queue.length} candidates handled · ${this.queue.length - this.pos} left in the list.`
    this.resumeTarget.hidden = !paused

    this.doneTarget.querySelectorAll<HTMLElement>("[data-stat]").forEach(stat => {
      const count = this.stats[stat.dataset.stat as StatKey] || 0
      const value = stat.querySelector("b")
      if (value) value.textContent = String(count)
      stat.hidden = count === 0
    })

    this.bodyTarget.hidden = true
    this.actionsTarget.hidden = true
    this.doneTarget.classList.add("show")
  }

  // --- outcomes ------------------------------------------------------------

  private askForTask(card: HTMLElement, outcome: Outcome, note: string): Promise<{ cancelled?: boolean; task?: FollowUpTask | null }> {
    const candidate = this.candidate(card)

    return new Promise(resolve => {
      window.dispatchEvent(new CustomEvent("call-task-dialog:open", {
        detail: {
          mode: "follow-up",
          candidateId: candidate.id,
          candidateName: candidate.name,
          outcome: FOLLOW_UP[outcome],
          callNote: this.plain(note),
          callLogId: this.dialer?.callLogId || undefined,
          resolve
        }
      }))
    })
  }

  private async interviewBooked(card: HTMLElement, note: string): Promise<void> {
    if (!card.dataset.inviteSent && document.querySelector("[data-controller~='interview-invite']")) {
      const answer = await this.askForInvite(card, note)
      if (answer.cancelled) return
      if (answer.invite === "sent") card.dataset.inviteSent = "1"
    }

    const logged = await this.log(card, "interview_booked", note, null)
    if (logged && card.dataset.inviteSent) this.stats.invites += 1
  }

  private askForInvite(card: HTMLElement, note: string): Promise<InviteAnswer> {
    const link = card.querySelector<HTMLAnchorElement>("[data-dialer-rec] a")
    return new Promise(resolve => {
      window.dispatchEvent(new CustomEvent("interview-invite:open", {
        detail: {
          candidate: this.candidate(card),
          callNote: this.plain(note),
          callLogId: this.dialer?.callLogId || null,
          recording: link ? { url: link.href, label: link.textContent || "Call recording" } : null,
          resolve
        }
      }))
    })
  }

  private async log(card: HTMLElement, outcome: Outcome, note: string, task: FollowUpTask | null): Promise<boolean> {
    const id = card.dataset.bpId || ""
    const body: Record<string, unknown> = { outcome, note, call_log_id: this.dialer?.callLogId || null }
    if (task) {
      body.task = { title: task.title, due_at: `${task.date}T${task.time || "10:00"}`, assignee_id: task.who, note: task.note }
    }

    let data: any
    try {
      const response = await fetch(this.outcomeUrlValue.replace(":id", encodeURIComponent(id)), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify(body)
      })
      data = await response.json().catch(() => null)
      if (!response.ok || !data?.ok) throw new Error((data?.errors || [])[0] || "The outcome wasn't saved.")
    } catch (error) {
      showToast((error as Error).message || "The outcome wasn't saved.", "error", 3500)
      return false
    }

    this.applyToRows(id, data)
    this.logged += 1
    this.stats[STAT_FOR[outcome]] += 1
    if (data.task_created) this.stats.tasks += 1
    this.next()
    return true
  }

  // §3.3: the row's Phone pill and Next task cell, its notes icon, and the selection.
  private applyToRows(id: string, data: any): void {
    rowsFor(id).forEach(row => {
      row.dataset.phone = data.status_calling || ""
      const phone = row.querySelector(".st-phone")
      if (phone && data.cells?.phone) phone.outerHTML = data.cells.phone
      const next = row.querySelector(".st-next")
      if (next && data.cells?.next_task) next.outerHTML = data.cells.next_task
    })
    syncRowNotes(id, !!data.has_notes)
    window.dispatchEvent(new CustomEvent("phone-grid:deselect", { detail: { id } }))
  }

  // --- helpers -------------------------------------------------------------

  private card(): HTMLElement | null {
    return this.bodyTarget.querySelector<HTMLElement>("[data-call-card]")
  }

  private candidate(card: HTMLElement): { id: string; name: string } {
    try {
      return JSON.parse(card.dataset.candidate || "{}")
    } catch (_e) {
      return { id: card.dataset.bpId || "", name: "" }
    }
  }

  // The leftover text in the card's note box, logged with the outcome (m3).
  private noteHtml(card: HTMLElement): string {
    const input = card.querySelector<HTMLTextAreaElement>("[data-note-input]")
    return input ? readNoteInput(input) : ""
  }

  private plain(html: string): string {
    const div = document.createElement("div")
    div.innerHTML = html
    return (div.textContent || "").trim()
  }

  private stopCall(): Promise<void> {
    return this.dialer?.isLive() ? this.dialer.hangUp() : Promise.resolve()
  }

  private teardownCard(): void {
    this.dialer?.destroy()
    this.dialer = null
  }

  private showSteps(): void {
    this.doneTarget.classList.remove("show")
    this.bodyTarget.hidden = false
    this.actionsTarget.hidden = false
  }

  private setActionsLocked(locked: boolean): void {
    this.actionsTarget.classList.toggle("locked", locked)
  }

  private doneShowing(): boolean {
    return this.doneTarget.classList.contains("show")
  }

  private isOpen(): boolean {
    return this.element.classList.contains("open")
  }

  private emptyStats(): Record<StatKey, number> {
    return { interview_booked: 0, callback: 0, no_answer: 0, not_interested: 0, tasks: 0, invites: 0 }
  }
}
